// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon';
import Button from '../Button';
import noTransactionsYet from '../../assets/icons/svg/large-icons/ic-large-categorization-reload.svg';
import { refactoredTimeAndDate } from '../../helpers/timeFormatter.helper';
import { CURRENCY, DATE_FORMAT, FORMAT_TYPE, LATEST_TRANSACTIONS_TEXT } from '../../constants/general';
import { addDotsWithNullIfNeeded, formatAmount } from '../../helpers/currency.helper';
import transactionIconPlaceholder from '../../assets/icons/svg/colored-icons/ic-colored-transaction.svg';
import { getIconFromTrxType } from './constants';
import { getTransactionStatus } from '../../flows/transactions/utils';
import { TRANSACTION_TYPES } from '../../flows/transactions/constants';
import { LatestTransactionsIcon } from '../../assets/svg';
import Heading from '../Heading';

interface IProps {
  data: any;
  isLoading: boolean;
  navPath: string;
  height?: string;
  wFull?: boolean;
  handleShowAll?: () => void;
}

interface ITransaction {
  merchantName: string;
  details: string;
  timestamp: string;
  amount: number;
  transactionId: string;
  trxType: string;
  trxTypeParams: string;
  onHold: boolean;
  externalTransactionType: string;
  transactionStatus: string;
  checkDetailsData: any;
  initiatorPan?: string;
  trxTitle?: string;
  trxDescription?: string;
}

const LatestTransactions = ({ data, isLoading, navPath, height, wFull, handleShowAll }: IProps) => {
  const navigate = useNavigate();
  const { LATEST_TRANSACTIONS, SHOW_ALL, NO_TRANSACTIONS } = LATEST_TRANSACTIONS_TEXT;

  const { t } = useTranslation();

  const trxDetails = (transaction: ITransaction) => {
    if (transaction.externalTransactionType === TRANSACTION_TYPES.EMERGENCY_BUCKET_AUTO_SAVE
      || transaction.externalTransactionType === TRANSACTION_TYPES.EMERGENCY_BUCKET_MANUAL_SAVE) {
      return transaction.trxTitle;
    }
    return transaction.merchantName || transaction.details;
  };

  return (
    <div className={`bg-bw00 w-full ${wFull ? 'w-full' : ''} ${height || 'h-fit min-h-[450px]'} rounded-lg`}>
      <div className='flex justify-between items-center h-[54px] mt-0.5 mx-0.5 pl-3.5 pr-1.5 pt-[18px] pb-[12px] hover:bg-bw02 rounded-[6px]'>
        <div className='flex items-center'>
          <Icon className='w-[32px] h-[32px]' iconId='latestTransactionsIcon' src={LatestTransactionsIcon} />
          <span id='latestTransactions' className='pl-2 flex-2 font-metricMedium text-base text-bw08'>
            {t(LATEST_TRANSACTIONS)}
          </span>
        </div>
        <Button
          id='latestTransactionsBtn'
          className='h-[30px] text-bw08 font-metric text-sm hover:text-action03 hover:bg-bw03 hover:rounded px-2 flex items-center'
          onClick={() => {
            if (handleShowAll) handleShowAll();
            else navigate(navPath);
          }}
          title={t(SHOW_ALL)}
        />
      </div>
      <div className='border-b border-bw06 opacity-30 w-full my-0.5 mb-[12px]' />

      <div className='w-full h-fit overflow-scroll pb-[12px]'>
        {!isLoading && data.length === 0 && (
          <div className='flex flex-col justify-center items-center gap-6 mt-[5rem]'>
            <img className='w-[9.5rem] h-[9.875rem]' src={noTransactionsYet} alt='no-transactions' />
            <span className='text-sm font-metric text-bw06 text-center px-14'>{t(NO_TRANSACTIONS)}</span>
          </div>
        )}
        {!isLoading && data.length > 0
          ? data.map(
            (
              transaction: {
                  merchantName: string;
                  details: string;
                  timestamp: string;
                  amount: number;
                  transactionId: string;
                  trxType: string;
                  trxTypeParams: string;
                  onHold: boolean;
                  externalTransactionType: string;
                  transactionStatus: string;
                  checkDetailsData: any;
                  initiatorPan?: string;
                },
              index: number,
            ) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                className='px-1'
                onClick={() => {
                  if (handleShowAll) {
                    handleShowAll();
                  }
                  navigate(navPath, { state: { preSelectedId: transaction.transactionId } });
                }}
                id={`transaction${index}Wrapper`}
                key={`${transaction.transactionId}`}
              >
                <div className='flex items-center hover:bg-bw02 hover:rounded cursor-pointer px-3 py-2 flex-wrap'>
                  <div className='inline-block mr-[0.625rem] gap-[6px]'>
                    <Icon
                      iconId={`transaction_${index}_icon`}
                      className='w-[2rem] min-w-[2rem]'
                      src={
                          getIconFromTrxType(transaction) !== ''
                            ? getIconFromTrxType(transaction)
                            : transactionIconPlaceholder
                        }
                    />
                    {transaction.initiatorPan && (
                    <Heading
                      title={`**${transaction.initiatorPan}`}
                      className='font-metricR text-action03 text-[10px] leading-[11px] font-medium'
                    />
                    )}
                  </div>
                  <div className='flex flex-col w-[calc(100%-42px)] flex-wrap'>
                    <div className='flex items-center justify-between'>
                      <span id={`transaction${index}Date`} className='text-sm font-metric text-bw06'>
                        {refactoredTimeAndDate(transaction.timestamp, 'dashboard', DATE_FORMAT.LETTERS)}
                      </span>
                      <div
                        id={`transaction${index}Amount`}
                        className={`font-metricMedium text-base text-right ${getTransactionStatus(transaction, t) === TRANSACTION_TYPES.CHECK_STATUS.REJECTED ? 'text-bw08' : transaction.amount > 0 ? 'text-badgeGreen03' : 'text-bw08'}`}
                      >
                        {`${formatAmount(addDotsWithNullIfNeeded(`${transaction.amount}`), FORMAT_TYPE.FORMATTED, CURRENCY.USD.SYMBOL, true, true).replaceAll('+', '')}`}
                      </div>
                    </div>
                    <span
                      id={`transaction${index}Details`}
                      className='text-base font-metricR text-bw08 whitespace-nowrap max-w-[80%] truncate'
                    >
                      {trxDetails(transaction)}
                    </span>
                  </div>
                </div>
                {index !== data.length - 1 && (
                <div className='border-b border-bw03 font-metricMedium w-auto mx-4 my-[12px]' />
                )}
              </div>
            ),
          )
          : isLoading && (
          <div className='w-full h-full overflow-hidden px-4'>
            <Skeleton className='my-4' count={8} />
          </div>
          )}
      </div>
    </div>
  );
};

export default LatestTransactions;
